import { createAction } from "@reduxjs/toolkit";
import { Action } from "redux";
import {
    createRsaaAction,
    type ICreateRsaaActionArgs,
} from "../../components/utilities/ReduxUtils";
import { getInlineEstimationContext } from "../../components/utilities/inlineEstimation";
import { IChemicalModel } from "../../data/ChemicalModel";
import type {
    SavedSearchRequest,
    SavedSearchResponse,
    WebSocketEstimateCompoundPayload,
} from "../../data/models/schemas";
import type { IScoreWeights } from "../reducers/persistent/scoreWeights";
import type { IUserState } from "../types/IUserState";
import { createSocketAction } from "../middlewares/websocket";
import type {
    IEstimateCompoundsResponse,
    ILoadCompoundsRequest,
    ILoadCompoundsResponse,
    ISearchCompoundsRequest,
    ISearchCompoundsResponse,
} from "../reducers/pocketbook";
import {
    refreshLicenseInfoMeta,
    withRemainingUnitsUpdateMeta,
} from "../utils/meteringMeta";

export const ADD_COMPOUND: string = "ADD_COMPOUND";
export const REMOVE_COMPOUND: string = "REMOVE_COMPOUND";
export const UPDATE_COMPOUND: string = "UPDATE_COMPOUND";
export const REMOVE_COMPOUNDS: string = "REMOVE_COMPOUNDS";
export const REPLACE_COMPOUNDS: string = "REPLACE_COMPOUNDS";
export const CLEAR_COMPOUNDS: string = "CLEAR_COMPOUNDS";
export const SEARCH_COMPOUNDS: string = "SEARCH_COMPOUNDS";
export const LOAD_COMPOUNDS: string = "LOAD_COMPOUNDS";
export const ESTIMATE_COMPOUND: string = "ESTIMATE_COMPOUND";
export const LOAD_SAVED_SEARCHES: string = "LOAD_SAVED_SEARCHES";
export const SAVE_CURRENT_SEARCH: string = "SAVE_CURRENT_SEARCH";
export const SET_PROPERTIES_TAB: string = "SET_PROPERTIES_TAB";
export const SET_GRAPH_TAB: string = "SET_GRAPH_TAB";
export const CLEAR_SEARCH_RESULTS: string = "CLEAR_SEARCH_RESULTS";

export const searchCompounds = createRsaaAction<
    ISearchCompoundsRequest,
    ISearchCompoundsResponse,
    ISearchCompoundsRequest
>({
    path: "chemicals/search",
    method: "POST",
    type: SEARCH_COMPOUNDS,
    meta: () => refreshLicenseInfoMeta,
});

export const loadCompounds = createRsaaAction<
    ILoadCompoundsRequest,
    ILoadCompoundsResponse,
    ILoadCompoundsResponse
>({
    path: "chemicals/data",
    method: "POST",
    type: LOAD_COMPOUNDS,
    meta: (p) =>
        withRemainingUnitsUpdateMeta({
            clearCompounds: p.requestContext?.clearCompounds,
        }),
});

export type IEstimatePayload = WebSocketEstimateCompoundPayload;

export const estimateCompound = createSocketAction<
    IEstimatePayload,
    IEstimateCompoundsResponse,
    IEstimateCompoundsResponse
>({
    type: ESTIMATE_COMPOUND,
    meta: refreshLicenseInfoMeta,
});

export const addCompound = createAction<IChemicalModel>(ADD_COMPOUND);

export const removeCompound = createAction<number>(REMOVE_COMPOUND);

export const updateCompound = createAction(UPDATE_COMPOUND);

export const removeCompounds = createAction(REMOVE_COMPOUNDS);

export const replaceCompounds =
    createAction<IChemicalModel[]>(REPLACE_COMPOUNDS);

export const clearCompounds = createAction(CLEAR_COMPOUNDS);

export const loadSavedSearches = createRsaaAction<void, SavedSearchResponse[]>({
    path: "listSearches",
    method: "POST",
    type: LOAD_SAVED_SEARCHES,
});

export type ISaveCurrentSearchRequest = SavedSearchRequest;

export const saveCurrentSearch = createRsaaAction<ISaveCurrentSearchRequest>({
    path: "saveSearch",
    method: "POST",
    type: SAVE_CURRENT_SEARCH,
});

export const setPropertiesTab = createAction<number | string>(
    SET_PROPERTIES_TAB
);

export const getGraphs = (): GraphContextAction[] => [
    {
        key: "ghs_radar",
        title: "GHS Analysis",
        type: "radar",
        source: "ghs",
        display: true,
    },
    {
        key: "viridischem_radar",
        title: "ViridisChem Analysis",
        type: "radar",
        source: "viridischem",
        default: true,
        display: true,
    },
    // {
    //     key: "viridischem_bar",
    //     title: "Score Compare",
    //     type: "bar",
    //     source: "viridischem",
    //     display: false,
    // },
];

export interface GraphContextAction extends Action<string> {
    key: string;
    default?: boolean;
    display?: boolean;
    title: string;
    type: string;
    source: string;
}

export const setGraphContext = createAction<GraphContextAction>(SET_GRAPH_TAB);

export const clearSearchResults = createAction(CLEAR_SEARCH_RESULTS);

export type ILoadCompoundsActionArgs = {
    compoundIds: Array<number | string>;
    clearCompounds: boolean;
    limit: number;
    weights?: IScoreWeights;
    user?: IUserState;
};

export const createLoadCompoundsActionArgs = ({
    compoundIds,
    clearCompounds,
    limit,
    weights,
    user,
}: ILoadCompoundsActionArgs): ICreateRsaaActionArgs<ILoadCompoundsRequest> => {
    const estimationContext = getInlineEstimationContext(user);

    return {
        body: {
            criteria: {
                compound_ids: compoundIds.map((id) => id.toString()),
                limit,
            },
            requestContext: {
                clearCompounds,
                weights,
            },
            ...(estimationContext ? { estimationContext } : {}),
        },
        ...(estimationContext
            ? {
                  meta: {
                      usesDataRequestEstimation: true,
                  },
              }
            : {}),
    };
};
